import React, { useCallback, useEffect, useState } from "react";

import { useGetLLMPassThroughMutation } from "../../../store/api/lumi/llm";
import { useGetDocumentsMutation } from "../../../store/api/lumi/documents";
import SummaryContainer from "../../Summary/SummaryContainer";
import SummaryHeader from "../../Summary/SummaryHeader";
import SummaryDocuments from "../../Summary/SummaryDocuments";
import SummaryText from "../../Summary/SummaryText";
import { useSearchParams } from "react-router-dom";
import { Collapse, Pagination, Tooltip } from "antd";
import { CopyOutlined, CheckOutlined } from "@ant-design/icons";
import LoadingIndicator from "../../Summary/LoadingIndicator";
import Scrollbars from "react-custom-scrollbars-2";
import {Typography } from 'antd';
const { Text} = Typography;
const CopyToClipboard = ({ content }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(content);
        setCopied(true);
        // message.success("Copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
      };

    return (
        <Tooltip title={copied ? "Copied!" : "Copy"}>
          {copied ? <CheckOutlined style={{ color: "green" }} /> : <CopyOutlined onClick={handleCopy} style={{ cursor: "pointer" }} />}
        </Tooltip>
    );

}

const NewSummaryText = ({ content, loading }) => {
    if (loading) {
        return <LoadingIndicator />
    }
    return (
        <p style={{ maxHeight:"150px", overflow:"auto", color:"#353935", lineHeight:"19px", margin:"0", padding:"0 5px"}}>{content || ""}</p>
    )
}

const NewSummaryDocuments = ({ loading, documents }) => {
    const [documentsIndex, setDocumentsIndex] = useState(1);
    if (loading) {
        return <LoadingIndicator />;
    }

    return (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <Scrollbars style={{ height: "180px" }}>
                {documents && documents.length > 0 ? (
                    <p style={{ maxHeight:"150px", overflow:"auto", color:"#353935", lineHeight:"19px", margin:"0", padding:"0 5px" }}>
                        {documents[documentsIndex - 1].text}
                    </p>
                ) : (
                    <p>no documents found</p>
                )}
            </Scrollbars>
            <div style={{textAlign:"center"}}>
            <Pagination
                simple={{
                    readOnly: true,
                }}
                defaultCurrent={2}
                current={documentsIndex}
                total={documents.length}
                onChange={(page) => setDocumentsIndex(page)}
                showSizeChanger={false}
                pageSize={1}
            />
            </div>
        </div>
    );

    
}

const LargestConceptMovementSummary = ({
                                           id,
                                           selectedConcept,
                                           filterBaseline,
                                           filterComparison,
                                           comparingDates
                                       }) => {
    const [triggerGetDocuments] = useGetDocumentsMutation();
    const [searchParams] = useSearchParams();
    const isNewApp = searchParams.get("app") === "new";
    const [triggerGetLLMPassThrough] = useGetLLMPassThroughMutation();

    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [baselineDocuments, setBaselineDocuments] = useState([]);
    const [comparisonDocuments, setComparisonDocuments] = useState([]);

    const [loadingConceptSummary, setLoadingConceptSummary] = useState(false);
    const [baselineSummary, setBaselineSummary] = useState(null);
    const [comparisonSummary, setComparisonSummary] = useState(null);

    const fetchDocuments = useCallback(async () => {
        // nothing to do if we don't have a selectedConcept or breakdown information
        if (!selectedConcept) return;

        setLoadingDocuments(true);
        try {
            const [resultBaseline, resultComparison] = await Promise.all([
                triggerGetDocuments({
                    id,
                    filter: filterBaseline,
                    search: selectedConcept.texts,
                }).unwrap(),
                triggerGetDocuments({
                    id,
                    filter: filterComparison,
                    search: selectedConcept.texts,
                }).unwrap(),
            ]);

            setBaselineDocuments(resultBaseline.documents);
            setComparisonDocuments(resultComparison.documents);
        } catch (error) {
            console.error("Failed to fetch documents:", error);

            setBaselineDocuments([]);
            setComparisonDocuments([]);
        } finally {
            setLoadingDocuments(false);
        }
    }, [triggerGetDocuments, id, selectedConcept]);

    useEffect(() => {
        fetchDocuments();
    }, [selectedConcept]);

    const handleLlmRequest = useCallback(
        async (prompt) => {
            try {
                const llmResponse = await triggerGetLLMPassThrough({
                    projectId: id,
                    prompt: prompt,
                }).unwrap();

                return llmResponse.response;
            } catch (error) {
                console.error("failed to fetch summary", error);
                return null;
            }
        },
        [id, prompt],
    );

    const fetchSummary = useCallback(async () => {
        if (!selectedConcept) return;
        if (baselineDocuments.length === 0 || comparisonDocuments.length === 0) return;

        setLoadingConceptSummary(true);
        try {
            const baselinePrompt =
                `Summarize how "${selectedConcept.texts.join(", ")}" is spoken about in these documents\n` +
                baselineDocuments.map((d) => `<p>${d.text}</p>`).join("");

            const comparisonPrompt =
                `Analyze and summarize the differences in how often "${selectedConcept.texts.join(", ")}" is mentioned or discussed between the two document sets provided below. Focus on comparing key themes, tones, contexts, and notable shifts or contrasts in the nature of mentions. Ensure the summary is structured clearly and references the specific dates of each document set for clarity.\n\n` +
                `Document Set from ${comparingDates[1]}:\n` +
                baselineDocuments
                    .slice(0, 25)
                    .map((d, index) => `${index + 1}. ${d.text}`)
                    .join("\n") +
                `\n\nDocument Set from ${comparingDates[0]}:\n` +
                comparisonDocuments
                    .slice(0, 25)
                    .map((d, index) => `${index + 1}. ${d.text}`)
                    .join("\n") +
                `\n\nPlease provide a structured comparison that emphasizes the differences between the two sets of documents in relation to "${selectedConcept.texts.join(", ")}." Use bullet points or paragraphs to highlight:\n` +
                `- Changes in the frequency of mentions.\n` +
                `- Shifts in tone, sentiment, or context.\n` +
                `- Key themes or ideas associated with the concept across the two time periods.\n` +
                `Avoid using the terms "Baseline" and "Comparison." Instead, refer to the document sets by their respective dates (${comparingDates[1]} and ${comparingDates[0]}).\n`;

            const [baselineResult, comparisonResult] = await Promise.all([
                handleLlmRequest(baselinePrompt),
                handleLlmRequest(comparisonPrompt),
            ]);

            setBaselineSummary(baselineResult);
            setComparisonSummary(comparisonResult);
        } catch (error) {
            console.error("Failed to fetch summaries:", error);
        } finally {
            setLoadingConceptSummary(false);
        }
    }, [baselineDocuments, comparisonDocuments, handleLlmRequest]);

    useEffect(() => {
        fetchSummary();
    }, [baselineDocuments]);

    if (!selectedConcept)
        return (
            <SummaryContainer>
                <SummaryHeader>
                    Select a concept to show detailed summary.
                </SummaryHeader>
            </SummaryContainer>
        );

    if (isNewApp) {
        const items = [
            {
                key: "1",
                label: <Text strong style={{fontSize: "16px", color:"#090A09", fontWeight:"700"}}>Baseline Summary </Text>,
                children: (
                    <NewSummaryText
                        loading={loadingConceptSummary}
                        content={baselineSummary}
                    />
                ),
                extra: <CopyToClipboard content={baselineSummary} />,
            },
            {
                key: "2",
                label: <Text strong style={{fontSize: "16px", color:"#090A09", fontWeight:"700"}}>Baseline Documents for {selectedConcept.name}</Text>,
                children: (
                    <NewSummaryDocuments
                        loading={loadingDocuments}
                        documents={baselineDocuments}
                    />
                ),
            },
            {
                key: "3",
                label: <Text strong style={{fontSize: "16px", color:"#090A09", fontWeight:"700"}}>Comparison Summary</Text>,
                children: (
                    <NewSummaryText
                        loading={loadingConceptSummary}
                        content={comparisonSummary}
                    />
                ),
                extra: <CopyToClipboard content={comparisonSummary} />,
            },
            {
                key: "4",
                label: <Text strong style={{fontSize: "16px", color:"#090A09", fontWeight:"700"}}>Comparison Documents for {selectedConcept.name}</Text>,
                children: (
                    <NewSummaryDocuments
                        loading={loadingDocuments}
                        documents={comparisonDocuments}
                    />
                ),
            },
        ];

        return <Collapse accordion ghost items={items} defaultActiveKey={["2"]}/>;
    }

    return (
        <>
            <SummaryText
                header="Baseline Summary"
                loading={loadingConceptSummary}
                summary={baselineSummary}
            />
            <SummaryDocuments
                header={`Baseline Documents for ${selectedConcept.name}`}
                loading={loadingDocuments}
                documents={baselineDocuments}
            />
            <SummaryText
                header="Comparison Summary"
                loading={loadingConceptSummary}
                summary={comparisonSummary}
            />
            <SummaryDocuments
                header={`Comparison Documents for ${selectedConcept.name}`}
                loading={loadingDocuments}
                documents={comparisonDocuments}
            />
        </>
    );
};

export default LargestConceptMovementSummary;
